import { cva, type VariantProps } from 'class-variance-authority'
import { type HTMLAttributes, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

/**
 * Status colours follow the map in Docs/06-DESIGN-SYSTEM.md §2 exactly, so a
 * "verified" badge is the same green everywhere in the product.
 */
export const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-caption font-medium ' +
    'whitespace-nowrap [&_svg]:size-3 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        neutral: 'bg-muted text-muted-foreground',
        info: 'bg-info-subtle text-info-subtle-foreground',
        success: 'bg-success-subtle text-success-subtle-foreground',
        warning: 'bg-warning-subtle text-warning-subtle-foreground',
        destructive: 'bg-destructive-subtle text-destructive-subtle-foreground',
        accent: 'bg-accent-subtle text-accent-subtle-foreground',
        outline: 'border border-border text-muted-foreground',
      },
      size: {
        sm: 'px-1.5 text-overline',
        md: 'px-2 py-0.5',
      },
    },
    defaultVariants: { variant: 'neutral', size: 'md' },
  },
)

export type BadgeProps = HTMLAttributes<HTMLSpanElement> &
  VariantProps<typeof badgeVariants> & {
    icon?: ReactNode
  }

export function Badge({ className, variant, size, icon, children, ...props }: BadgeProps): ReactNode {
  return (
    <span className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {icon}
      {children}
    </span>
  )
}
